import { useState } from 'react';

import { useLazyQuery } from '@apollo/client/react';
import { Box } from '@mui/material';
import { useForm } from 'react-hook-form';

import { gql } from '@graphql/__generated__';
import {
  AI_LANGUAGE_OPTIONS,
  AiDescriptionMenu,
} from '@modules/_shared/components/AiDescriptionMenu';
import { RaSelect } from '@modules/_shared/components/form/RaSelect';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { useLocale } from '@shared/hooks/locale';

const GENERATE_TITLE = gql(/* GraphQL */ `
  query LotGenerateTitle($lot_id: uuid!, $language: String) {
    generate_listing_title(lot_id: $lot_id, language: $language) {
      title
    }
  }
`);

type LotGenerateTitleProps = {
  lot_id: string | null | undefined;
  onComplete: (title: string) => void;
};

type FormValues = {
  language: string;
};

export const LotGenerateTitle = ({
  lot_id,
  onComplete,
}: LotGenerateTitleProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const { t } = useLocale();
  const { control, watch } = useForm<FormValues>({
    defaultValues: { language: '' },
  });
  const language = watch('language');

  const [generateTitle, { loading }] = useLazyQuery(GENERATE_TITLE, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const applyGeneratedTitle = (value: string | null | undefined) => {
    if (value) {
      onComplete(value);
    }
  };

  const handleDialogSave = () => {
    setDialogOpen(false);
    generateTitle({
      variables: {
        lot_id: lot_id ?? '',
        language: language || null,
      },
    })
      .then(({ data }) => {
        applyGeneratedTitle(data?.generate_listing_title?.title);
      })
      // The hook's `error` state already drives the UI. Catch the v4
      // promise rejection so it doesn't surface as an unhandled rejection.
      .catch(() => {});
  };

  return (
    <>
      <AiDescriptionMenu
        onGenerate={
          lot_id != null
            ? () => {
                generateTitle({
                  variables: {
                    lot_id,
                    language: language || null,
                  },
                })
                  .then(({ data }) => {
                    applyGeneratedTitle(data?.generate_listing_title?.title);
                  })
                  .catch(() => {});
              }
            : undefined
        }
        onExpertMode={lot_id != null ? () => setDialogOpen(true) : undefined}
        isLoading={loading}
      />
      <SlideDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={t('Expert mode')}
        onSave={handleDialogSave}
        saveText={t('ok')}
        saveIcon={null}
        maxWidth="xs"
      >
        <Box sx={{ p: 2 }}>
          <RaSelect
            name="language"
            label={t('Language')}
            control={control}
            options={AI_LANGUAGE_OPTIONS}
          />
        </Box>
      </SlideDialog>
    </>
  );
};
